/**
 * Routing App - File Schemas Configuration
 *
 * Multi-tenant SaaS schemas for Walmart/Sam's routing system.
 * Dataset is dynamic based on tenant: tenant_{tenantId}
 */

/**
 * History Modes:
 * - 'replace': Truncate table on each upload (no history)
 * - 'daily_snapshot': Keep one copy per day (replace same-day uploads, keep previous days)
 */

// Schema definitions (dataset is injected at runtime)
export const ROUTING_SCHEMAS = {
  // ERP Orders - Your system's order data
  routing_erp_orders: {
    name: 'ERP Orders',
    icon: '📋',
    category: 'Routing',
    section: 'Order Data',
    tableName: 'routing_erp_orders',
    historyMode: 'replace', // Large files, no history needed
    maxSizeGB: 2,
    description: 'ERP order data for routing - POs, pick tickets, amounts',
    columns: [
      'po_number', 'pick_number', 'division', 'customer_name', 'order_status',
      'ship_window_start', 'ship_window_end', 'style', 'color', 'label', 'dim',
      'order_qty', 'net_ext_price', 'ext_gp', 'estimate_cartons', 'warehouse'
    ],
    requiredColumns: [
      'po_number', 'pick_number'
    ]
  },

  // Supplier One Orders - Walmart's view of orders
  routing_supplier_one: {
    name: 'Supplier One Orders',
    icon: '✳',
    category: 'Routing',
    section: 'Order Data',
    tableName: 'routing_supplier_one',
    historyMode: 'replace', // Large files, no history needed
    maxSizeGB: 2,
    description: 'Walmart Supplier One portal data - alternative to ERP orders',
    columns: [
      'po_number', 'po_type', 'department', 'mabd', 'ship_window_start',
      'ship_window_end', 'order_qty', 'unit_cost', 'total_cost', 'status',
      'dnsb', 'dnsa', 'dnrb', 'supplier_name', 'item_number'
    ],
    requiredColumns: [
      'po_number'
    ]
  },

  // Warehouse Ship Logs
  routing_warehouse_logs: {
    name: 'Warehouse Ship Logs',
    icon: '📦',
    category: 'Routing',
    section: 'Warehouse Data',
    tableName: 'routing_warehouse_logs',
    historyMode: 'replace', // Can be large, no history needed
    maxSizeGB: 1,
    description: 'Warehouse ship logs and pick status data',
    columns: [
      'pick_ticket', 'route_date', 'loads', 'ship_date', 'csr_comments',
      'img_notes', 'warehouse_code', 'carrier_name', 'tracking_number'
    ],
    requiredColumns: [
      'pick_ticket'
    ]
  },

  // Walmart TMS Data
  routing_walmart_tms: {
    name: 'Walmart TMS',
    icon: '🚛',
    category: 'Routing',
    section: 'Customer TMS',
    tableName: 'routing_walmart_tms',
    historyMode: 'daily_snapshot', // Small files, keep daily history for compliance
    maxSizeGB: 2,
    description: 'Walmart TMS routing data with load and carrier info',
    columns: [
      'po_number', 'load_id', 'load_dest', 'load_dest_type', 'load_dest_address',
      'load_p_u_window_start', 'load_p_u_window_end', 'mabd', 'carrier_pu_date',
      'carrier_due_date', 'carrier_name', 'industry_scac', 'mode', 'ship_point',
      'loading_method', 'cases', 'pallets', 'weight', 'cube', 'event_code',
      'po_type', 'department', 'confirmed_date', 'held_suspended', 'po_dest'
    ],
    requiredColumns: [
      'po_number'
    ]
  },

  // Store/DC Location Data
  routing_store_data: {
    name: 'Store/DC Data',
    icon: '🏢',
    category: 'Routing',
    section: 'Customer TMS',
    tableName: 'routing_store_data',
    historyMode: 'replace', // Reference data - no history needed
    maxSizeGB: 1,
    description: 'Walmart/Sam\'s DC and store location data',
    columns: [
      'location_id', 'location_name', 'location_type', 'address', 'city',
      'state', 'zip', 'country', 'latitude', 'longitude', 'region'
    ],
    requiredColumns: [
      'location_id', 'location_type'
    ]
  },

  // Transit Time Data
  routing_transit_time: {
    name: 'Transit Time',
    icon: '⏱',
    category: 'Routing',
    section: 'Customer TMS',
    tableName: 'routing_transit_time',
    historyMode: 'replace', // Reference data - no history needed
    maxSizeGB: 1,
    description: 'Transit time from origin warehouse to destination by mode',
    columns: [
      'origin_code', 'origin_name', 'destination_id', 'destination_name',
      'destination_type', 'mode', 'transit_days', 'effective_date', 'notes'
    ],
    requiredColumns: [
      'origin_code', 'destination_id', 'mode', 'transit_days'
    ]
  }
};

/**
 * Get schema with tenant-specific dataset
 * @param {string} schemaKey - The schema key (e.g., 'routing_walmart_tms')
 * @param {string} tenantId - The tenant ID (e.g., 'inmocean', 'acme')
 * @param {string} projectId - The Firebase project ID (read from FirebaseService.getProjectId())
 * @returns {object} Schema with datasetId set for the tenant
 */
export function getRoutingSchema(schemaKey, tenantId, projectId) {
  const schema = ROUTING_SCHEMAS[schemaKey];
  if (!schema) {
    throw new Error(`Unknown routing schema: ${schemaKey}`);
  }

  return {
    ...schema,
    datasetId: `tenant_${tenantId}`,
    // Full table reference for BigQuery
    fullTableId: `${projectId}.tenant_${tenantId}.${schema.tableName}`
  };
}

/**
 * Get all routing schemas for a tenant
 * @param {string} tenantId - The tenant ID
 * @param {string} projectId - The Firebase project ID (read from FirebaseService.getProjectId())
 * @returns {object} All schemas with tenant-specific datasetId
 */
export function getAllRoutingSchemas(tenantId, projectId) {
  const schemas = {};
  for (const key of Object.keys(ROUTING_SCHEMAS)) {
    schemas[key] = getRoutingSchema(key, tenantId, projectId);
  }
  return schemas;
}

/**
 * Get storage path for tenant uploads
 * @param {string} tenantId - The tenant ID
 * @param {string} fileType - The file type (e.g., 'routing_walmart_tms')
 * @returns {string} Storage path
 */
export function getStoragePath(tenantId, fileType) {
  return `tenants/${tenantId}/routing/${fileType}`;
}

/**
 * Validation rules for routing uploads
 */
export const ROUTING_VALIDATION_RULES = {
  MAX_FILE_SIZE_BYTES: 2 * 1024 * 1024 * 1024, // 2GB
  SUPPORTED_FORMATS: ['.csv', '.xlsx', '.xls'],
  SUPPORTED_MIME_TYPES: [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ]
};

export default ROUTING_SCHEMAS;
